import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Settings from "@/pages/Settings";
import AdminUsers from "@/pages/AdminUsers";
import AIInsights2 from "@/pages/AIInsights2";
import AILearning from "@/pages/AILearning";
import Campaigns from "@/pages/Campaigns";
import CampaignDetail from "@/pages/CampaignDetail";
import SalesPitch from "@/pages/SalesPitch";
import ContactUs from "@/pages/ContactUs";
import ManualOnboarding from "@/pages/ManualOnboarding";
import GettingStarted from "@/pages/GettingStarted";
import SwitchLocation from "@/pages/SwitchLocation";
import LocationManagement from "@/pages/LocationManagement";
import LandingPage from "@/pages/LandingPage";
import GoingHome from "@/pages/GoingHome";
import GoingHomeDisplay from "@/pages/GoingHomeDisplay";
import NoLocationAccess from "@/pages/NoLocationAccess";
import FollowUpQueue from "@/pages/FollowUpQueue";
import DailyOps from "@/pages/DailyOps";
import ExecDashboard from "@/pages/ExecDashboard";
import StackedExecDashboard from "@/pages/StackedExecDashboard";
import TenantPrototype from "@/pages/TenantPrototype";
import TenantManagement from "@/pages/TenantManagement";
import SignupRequests from "@/pages/SignupRequests";
import CommandCenter from "@/pages/CommandCenter";
import RolloutCenter from "@/pages/RolloutCenter";
import Setup from "@/pages/Setup";
import MyLocations from "@/pages/MyLocations";
import WhatsNew from "@/pages/WhatsNew";
import CustomerIntelligence from "@/pages/CustomerIntelligence";
import ComplianceAgent from "@/pages/ComplianceAgent";
import Agents from "@/pages/Agents";
import AgentSetup from "@/pages/AgentSetup";
import SmartLeadFollowUpAgent from "@/pages/SmartLeadFollowUpAgent";
import ChurnNudgeAgent from "@/pages/ChurnNudgeAgent";
import Layout from "@/components/Layout";
import MockupIndex from "@/mockups/MockupIndex";
import ExecMockup from "@/mockups/ExecMockup";
import RenewalsMockup from "@/mockups/RenewalsMockup";
import LeadsMockup from "@/mockups/LeadsMockup";
import GoingHomeMockup from "@/mockups/GoingHomeMockup";
import ReviewPage from "@/mockups/ReviewPage";
import KpiGradientMockup from "@/mockups/KpiGradientMockup";
import CampaignMockup from "@/mockups/CampaignMockup";
import LeadsExecMockup from "@/mockups/LeadsExecMockup";
import { LeadsDashboardSwitch } from "@/components/leadsexec/LeadsDashboardSwitch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults for better stability
axios.defaults.timeout = 30000; // 30 second default timeout

// Retry configuration
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

// Helper function to delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// STATIC request interceptor: adds Authorization token AND X-Location-Id to every request
// This runs outside React lifecycle so it's never ejected/re-added
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // ALWAYS use localStorage as the source of truth for location
    // This ensures location context is correct even if axios.defaults has a stale value
    const locationId = localStorage.getItem('currentLocationId');
    if (locationId) {
      config.headers['X-Location-Id'] = locationId;
    }
    // Never let the browser replay a STALE cached API response. Returning users
    // (already-logged-in Chrome/Firefox) were seeing pre-fix /api/going-home
    // JSON from the HTTP cache — no package_status — while incognito fetched
    // fresh. Forcing no-cache on GETs guarantees every poll hits the server.
    const method = (config.method || 'get').toLowerCase();
    if (method === 'get') {
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['Pragma'] = 'no-cache';
    }
    // Track retry count
    config.retryCount = config.retryCount || 0;
    return config;
  },
  (error) => Promise.reject(error)
);

// STATIC response interceptor: handle 401 (expired token) and retry on network errors
// IMPORTANT: Only clear auth data, NEVER clear currentLocationId
// so that after re-login the location context is preserved
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const requestUrl = config?.url || '';
    const isAuthEndpoint = requestUrl.includes('/auth/login') || 
                           requestUrl.includes('/auth/register') ||
                           requestUrl.includes('/auth/forgot-password') ||
                           requestUrl.includes('/auth/reset-password');
    
    // Handle 401 - token expired
    if (error.response?.status === 401 && !isAuthEndpoint) {
      const hasToken = localStorage.getItem("token");
      if (hasToken) {
        console.error('Token expired or invalid - clearing auth (preserving location)');
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("cachedUser");
        // CRITICAL: Do NOT remove currentLocationId — preserve it for re-login
        window.location.href = '/login?session_expired=true';
      }
      return Promise.reject(error);
    }
    
    // Retry on network errors or 5xx server errors (except 501)
    const isNetworkError = !error.response && error.code !== 'ECONNABORTED';
    const isServerError = error.response?.status >= 500 && error.response?.status !== 501;
    const isRetryable = (isNetworkError || isServerError) && config && config.retryCount < MAX_RETRIES;
    
    if (isRetryable && !isAuthEndpoint) {
      config.retryCount += 1;
      console.warn(`Request failed, retrying (${config.retryCount}/${MAX_RETRIES}): ${requestUrl}`);
      await delay(RETRY_DELAY * config.retryCount);
      return axios(config);
    }
    
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Auto-update gate ────────────────────────────────────────────────
  // Already-logged-in browsers can hold stale cached client state / assets
  // after a deploy (fresh incognito always worked, existing sessions didn't).
  // On load we ask the server for its build stamp; if it changed since we last
  // saw it, we purge our SWR localStorage caches and hard-reload ONCE so every
  // user picks up the new build without manually clearing cache.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/version`, { headers: { 'Cache-Control': 'no-cache' } })
      .then((res) => {
        if (cancelled) return;
        const serverBuild = res.data && res.data.build;
        if (!serverBuild) return;
        const seen = localStorage.getItem('app_build');
        if (seen && seen !== serverBuild) {
          // New deploy detected — drop stale cached board/package state.
          Object.keys(localStorage)
            .filter((k) => k.startsWith('swr_'))
            .forEach((k) => localStorage.removeItem(k));
          const reloadedFor = sessionStorage.getItem('reloaded_for_build');
          localStorage.setItem('app_build', serverBuild);
          if (reloadedFor !== serverBuild) {
            sessionStorage.setItem('reloaded_for_build', serverBuild);
            window.location.reload();
          }
        } else if (!seen) {
          localStorage.setItem('app_build', serverBuild);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);


  useEffect(() => {
    checkAuth();
    
    // Auto-refresh auth when tab becomes visible after being hidden
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const token = localStorage.getItem("token");
        if (token) {
          console.log('Tab visible - re-checking auth');
          checkAuth();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    
    // Keep-alive ping every 2 minutes to prevent connection drops
    const keepAliveInterval = setInterval(() => {
      const token = localStorage.getItem("token");
      if (token && document.visibilityState === 'visible') {
        axios.get(`${API}/health`).catch(() => {
          // Silent fail - just keeping connection warm
        });
      }
    }, 120000); // 2 minutes
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
      clearInterval(keepAliveInterval);
    };
  }, []); // Empty deps: runs once on mount, no interceptor churn

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }

    // Silent retry — user should never see "connecting" messages
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        
        const currentLocationId = localStorage.getItem('currentLocationId');
        const userLocations = response.data.locations || [];
        
        if (userLocations.length > 0) {
          const storedLocationValid = currentLocationId && 
            userLocations.some(loc => loc.id === currentLocationId);
          if (!storedLocationValid) {
            const locationToSet = response.data.location_id || userLocations[0].id;
            localStorage.setItem('currentLocationId', locationToSet);
          }
        }
        
        localStorage.setItem('cachedUser', JSON.stringify(response.data));
        setUser(response.data);
        setLoading(false);
        return;
      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("cachedUser");
          setUser(null);
          setLoading(false);
          return;
        }
        // Silent retry on network/server errors
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
          continue;
        }
        // Final failure: restore from cache so app stays functional
        const cachedUser = localStorage.getItem('cachedUser');
        if (cachedUser) {
          try { setUser(JSON.parse(cachedUser)); } catch (e) { /* ignore corrupt cache */ }
        }
      }
    }
    setLoading(false);
  };

  const handleLogin = async (userData, token) => {
    localStorage.setItem("token", token);
    
    // Set location from the login response data we already have
    if (userData.locations && userData.locations.length > 0) {
      const existingLocation = localStorage.getItem('currentLocationId');
      const validExisting = existingLocation && 
        userData.locations.some(loc => loc.id === existingLocation);
      if (!validExisting) {
        const locationToSet = userData.location_id || userData.locations[0].id;
        localStorage.setItem('currentLocationId', locationToSet);
      }
    }
    
    // Use login response directly — NO second API call needed
    localStorage.setItem('cachedUser', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("currentLocationId");
    localStorage.removeItem("selectedLocationId");
    localStorage.removeItem("selectedLocationName");
    localStorage.removeItem("cachedUser");
    setUser(null);
    // Redirect to landing page after logout
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="text-xl font-medium text-gray-600">Loading...</div>
      </div>
    );
  }

  // Helper function to check if user has location access
  const hasLocationAccess = (user) => {
    return user && user.locations && user.locations.length > 0;
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/mockups" element={<MockupIndex />} />
          <Route path="/mockups/exec" element={<ExecMockup />} />
          <Route path="/mockups/renewals" element={<RenewalsMockup />} />
          <Route path="/mockups/leads" element={<LeadsMockup />} />
          <Route path="/mockups/goinghome" element={<GoingHomeMockup />} />
          <Route path="/mockups/review" element={<ReviewPage />} />
          <Route path="/mockups/kpi-gradients" element={<KpiGradientMockup />} />
          <Route path="/mockups/campaigns" element={<CampaignMockup />} />
          <Route path="/mockups/leads-exec" element={<LeadsExecMockup />} />
          {/* Public Landing Page - Root Path */}
          <Route
            path="/"
            element={
              !user ? (
                <LandingPage />
              ) : hasLocationAccess(user) ? (
                <Navigate to="/ops" replace />
              ) : (
                <Navigate to="/no-location-access" replace />
              )
            }
          />
          
          {/* No Location Access Page */}
          <Route
            path="/no-location-access"
            element={
              user && !hasLocationAccess(user) ? (
                <NoLocationAccess user={user} onLogout={handleLogout} />
              ) : user && hasLocationAccess(user) ? (
                <Navigate to="/ops" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          
          {/* Authentication Routes */}
          <Route
            path="/login"
            element={
              !user ? (
                <Login onLogin={handleLogin} />
              ) : hasLocationAccess(user) ? (
                <Navigate to="/ops" replace />
              ) : (
                <Navigate to="/no-location-access" replace />
              )
            }
          />
          <Route
            path="/forgot-password"
            element={
              !user ? (
                <ForgotPassword />
              ) : (
                <Navigate to="/ops" replace />
              )
            }
          />
          <Route
            path="/reset-password"
            element={
              !user ? (
                <ResetPassword />
              ) : (
                <Navigate to="/ops" replace />
              )
            }
          />
          
          {/* Protected Exec Dashboard Route - Admin/Manager/Super Admin only */}
          <Route
            path="/exec-dashboard"
            element={
              user ? (
                hasLocationAccess(user) ? (
                  (user.role === 'admin' || user.role === 'super_admin' || user.role === 'tenant_admin') ? (
                    <Layout user={user} onLogout={handleLogout}>
                      <ExecDashboard />
                    </Layout>
                  ) : (
                    <Navigate to="/ops" replace />
                  )
                ) : (
                  <Navigate to="/no-location-access" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Protected Dashboard Route */}
          <Route
            path="/dashboard"
            element={
              user ? (
                hasLocationAccess(user) ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <LeadsDashboardSwitch />
                  </Layout>
                ) : (
                  <Navigate to="/no-location-access" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/going-home"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <GoingHome />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/going-home/display"
            element={<GoingHomeDisplay />}
          />
          <Route
            path="/customer-intelligence"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CustomerIntelligence />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/agents"
            element={
              user ? (
                (user.role === 'admin' || user.role === 'super_admin' || user.role === 'tenant_admin') ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <div className="ops-v3"><Agents /></div>
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/agents/vaccine-compliance"
            element={
              user ? (
                (user.role === 'admin' || user.role === 'super_admin' || user.role === 'tenant_admin') ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <div className="ops-v3"><ComplianceAgent agentType="vaccine_compliance" /></div>
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/agents/unsigned-agreements"
            element={
              user ? (
                (user.role === 'admin' || user.role === 'super_admin' || user.role === 'tenant_admin') ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <div className="ops-v3"><ComplianceAgent agentType="unsigned_agreements" /></div>
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/agents/churn-nudge"
            element={
              user ? (
                (user.role === 'admin' || user.role === 'super_admin' || user.role === 'tenant_admin' || user.role === 'manager') ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <div className="ops-v3"><ChurnNudgeAgent /></div>
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/agents/smart-lead-followup"
            element={
              user ? (
                (user.role === 'admin' || user.role === 'super_admin' || user.role === 'tenant_admin' || user.role === 'manager') ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <div className="ops-v3"><SmartLeadFollowUpAgent /></div>
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route path="/smart-leads" element={<Navigate to="/follow-up-queue" replace />} />
          <Route
            path="/agent-setup"
            element={
              user ? (
                (user.role === 'admin' || user.role === 'super_admin' || user.role === 'tenant_admin') ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <AgentSetup />
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/compliance-agent"
            element={<Navigate to="/agents/vaccine-compliance" replace />}
          />
          <Route
            path="/leads"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Leads />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/leads/:id"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <LeadDetail />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/follow-up-queue"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <FollowUpQueue />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/ops"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <DailyOps />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <div className="ops-v3"><Settings /></div>
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/users"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <div className="ops-v3"><AdminUsers /></div>
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/ai-insights"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <AIInsights2 />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/campaigns"
            element={user ? (<Layout user={user} onLogout={handleLogout}><Campaigns /></Layout>) : (<Navigate to="/login" replace />)}
          />
          <Route
            path="/campaigns/:id"
            element={user ? (<Layout user={user} onLogout={handleLogout}><CampaignDetail /></Layout>) : (<Navigate to="/login" replace />)}
          />
          <Route
            path="/ai-insights/learned"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <AILearning />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/sales-pitch"
            element={
              user ? (
                user.role === 'admin' && !user.location_id ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <SalesPitch />
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/contact-us"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ContactUs />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/onboard"
            element={
              user ? (
                user.role === 'super_admin' ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <div className="ops-v3"><ManualOnboarding /></div>
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/getting-started"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <GettingStarted />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/switch-location"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <SwitchLocation />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/exec-dashboard/tenant"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <StackedExecDashboard />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/tenants"
            element={
              user ? (
                user.role === 'super_admin' ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <div className="ops-v3"><TenantManagement /></div>
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/signup-requests"
            element={
              user ? (
                user.role === 'super_admin' ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <div className="ops-v3"><SignupRequests /></div>
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/admin/rollout"
            element={user ? (user.role === 'super_admin' ? <Layout user={user} onLogout={handleLogout}><RolloutCenter /></Layout> : <Navigate to="/ops" replace />) : <Navigate to="/login" replace />}
          />
          <Route
            path="/setup"
            element={user ? <Layout user={user} onLogout={handleLogout}><Setup /></Layout> : <Navigate to="/login" replace />}
          />
          <Route
            path="/admin/command-center"
            element={<Navigate to="/admin/rollout?tab=command" replace />}
          />
          <Route
            path="/admin/command-center-legacy"
            element={
              user ? (
                user.role === 'super_admin' ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <div className="ops-v3"><CommandCenter /></div>
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Read-only prototype for multi-tenant rollout review (Super Admin only) */}
          <Route
            path="/prototype/tenant-view"
            element={
              user ? (
                user.role === 'super_admin' ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <TenantPrototype />
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/location-management"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <div className="ops-v3"><LocationManagement /></div>
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Phase D — Tenant-Admin self-serve. Allowed: tenant_admin, admin, super_admin */}
          <Route
            path="/settings/my-locations"
            element={
              user ? (
                ['tenant_admin', 'admin', 'super_admin'].includes(user.role) ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <div className="ops-v3"><MyLocations /></div>
                  </Layout>
                ) : (
                  <Navigate to="/ops" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* What's New / Ideas Board — visible to every authenticated user */}
          <Route
            path="/whats-new"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout}>
                  <WhatsNew />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
